import type { WithheldTiers } from '@/types/waterfall'
import { R2 } from '@/utils/waterfallGrid'

const THRESHOLD_KEY = 'waterfall.vestingCatchUpThreshold'

export const DEFAULT_VESTING_CATCH_UP_THRESHOLD = 100

type VestingAccountLike = {
  payable_at_next_vest?: number
  projected_payable_at_next_vest?: number
  cumulative_withheld?: number
  withheld_balance?: number
  payable_from_new_cash?: number
  last_pool_event_num?: number | null
  last_pool_event_date?: string | null
}

/** Ensure renamed vesting account fields survive cached calculator snapshots. */
export function normalizeVestingAccount<T extends VestingAccountLike>(
  account: T,
): T & {
  payable_at_next_vest: number
  cumulative_withheld: number
  withheld_balance: number
  payable_from_new_cash: number
  last_pool_event_num: number | null
  last_pool_event_date: string | null
} {
  const raw = account.payable_at_next_vest ?? account.projected_payable_at_next_vest ?? 0
  const payable_at_next_vest =
    typeof raw === 'number' && Number.isFinite(raw) ? raw : 0
  const num = (v: unknown) =>
    typeof v === 'number' && Number.isFinite(v) ? v : 0
  return {
    ...account,
    payable_at_next_vest,
    cumulative_withheld: num(account.cumulative_withheld),
    withheld_balance: num(account.withheld_balance),
    payable_from_new_cash: num(account.payable_from_new_cash),
    last_pool_event_num:
      account.last_pool_event_num === undefined || account.last_pool_event_num === null
        ? null
        : account.last_pool_event_num,
    last_pool_event_date: account.last_pool_event_date ?? null,
  }
}

export function normalizeVestingAccounts<T extends VestingAccountLike>(
  accounts: T[],
): ReturnType<typeof normalizeVestingAccount<T>>[] {
  return accounts.map(normalizeVestingAccount)
}

export function loadVestingCatchUpThreshold(): number {
  try {
    const raw = localStorage.getItem(THRESHOLD_KEY)
    if (raw === null) return DEFAULT_VESTING_CATCH_UP_THRESHOLD
    const parsed = Number(raw)
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : DEFAULT_VESTING_CATCH_UP_THRESHOLD
  } catch {
    return DEFAULT_VESTING_CATCH_UP_THRESHOLD
  }
}

export function saveVestingCatchUpThreshold(value: number): void {
  localStorage.setItem(THRESHOLD_KEY, String(Math.max(0, value)))
}

export function isCatchUpReady(proFormaPayable: number, threshold: number): boolean {
  return proFormaPayable > 0 && proFormaPayable >= threshold
}

export function splitCatchUp(C: number, vN: number): { c1: number; c2: number } {
  const c = Math.max(0, C)
  const c1 = R2(Math.min(c, Math.max(0, vN)))
  const c2 = R2(Math.max(0, c - c1))
  return { c1, c2 }
}

export function effectiveWithheld(proposalWithheld: WithheldTiers, c1: number): WithheldTiers {
  let remaining = Math.max(0, c1)
  const s3b = { ...proposalWithheld.s3b }
  const s4b = { ...proposalWithheld.s4b }
  const smS3 = s3b.SM
  const cutS3 = Math.min(smS3, remaining)
  s3b.SM = R2(smS3 - cutS3)
  remaining = R2(remaining - cutS3)
  if (remaining > 0.005) {
    const smS4 = s4b.SM
    const cutS4 = Math.min(smS4, remaining)
    s4b.SM = R2(smS4 - cutS4)
  }
  return { s3b, s4b }
}

export function prefillCatchUpAmount(proFormaPayable: number, threshold: number): number {
  return proFormaPayable >= threshold && proFormaPayable > 0.005 ? proFormaPayable : 0
}

export function buildCatchUpLogFields({
  mode,
  proposalWithheld,
  C,
  vN,
}: {
  mode: 'this' | 'separate'
  proposalWithheld: WithheldTiers
  C: number
  vN: number
}): { withheld: WithheldTiers; catch_up_addons?: { member: 'SM'; amount: number }[] } {
  if (mode === 'separate') {
    return { withheld: proposalWithheld }
  }
  const { c1, c2 } = splitCatchUp(C, vN)
  const withheld = effectiveWithheld(proposalWithheld, c1)
  return {
    withheld,
    catch_up_addons: c2 > 0.005 ? [{ member: 'SM', amount: c2 }] : undefined,
  }
}
